import { useEffect, useRef } from 'react';
import { Coordinate } from '../model/GraphicModel';

type Props = {
	parentContainerId: string;
	targetContainerId: string;
	targetDragContainerId?: string;
	initialCoordinates?: Coordinate;
	parentCoordinates?: Coordinate;
	onDragEnd?: (coordinate: Coordinate) => void;
};

const useDraggable = ({ parentContainerId, targetContainerId, targetDragContainerId, initialCoordinates, parentCoordinates, onDragEnd }: Props) => {
	const parentContainer = useRef<HTMLElement>(null);
	const targetContainer = useRef<HTMLElement>(null);
	const targetDragContainer = useRef<HTMLElement>(null);
	const isContainerClicked = useRef<boolean>(false);
	const coordinates = useRef<Coordinate>({ ...initialCoordinates });
	const lastCoordinates = useRef<Coordinate>({ x: 0, y: 0 });

	useEffect(() => {
		parentContainer.current = document.getElementById(parentContainerId);
		targetContainer.current = document.getElementById(targetContainerId);
		targetDragContainer.current = document.getElementById(targetDragContainerId);

		if (!parentContainer.current || !targetContainer.current || !targetDragContainer.current) {
			return;
		}

		targetContainer.current.addEventListener('mouseup', handleOnDragMouseUp);
		targetContainer.current.addEventListener('mousedown', handleOnDragMouseDown);
		parentContainer.current.addEventListener('mousemove', handleOnDragMouseMove);
		parentContainer.current.addEventListener('mouseleave', handleOnDragMouseUp);
		targetDragContainer.current.addEventListener('mouseenter', handleOnMouseEnter);
		targetDragContainer.current.addEventListener('mouseleave', handleOnMouseLeave);
		let newCoordinateX: number = initialCoordinates.x;
		let newCoordinateY: number = initialCoordinates.y;
		if (initialCoordinates.x < 0 && initialCoordinates.y < 0) {
			newCoordinateX = parentCoordinates.x - targetContainer.current.offsetWidth;
			newCoordinateY = 2;
			targetContainer.current.style.left = `${newCoordinateX}px`;
			targetContainer.current.style.top = `${newCoordinateY}px`;
		}
		lastCoordinates.current.y = newCoordinateY;
		lastCoordinates.current.x = newCoordinateX;

		return () => {
			targetContainer.current.removeEventListener('mouseup', handleOnDragMouseUp);
			targetContainer.current.removeEventListener('mousedown', handleOnDragMouseDown);
			parentContainer.current.removeEventListener('mousemove', handleOnDragMouseMove);
			parentContainer.current.removeEventListener('mouseleave', handleOnDragMouseUp);
			targetDragContainer.current.removeEventListener('mouseenter', handleOnMouseEnter);
			targetDragContainer.current.removeEventListener('mouseleave', handleOnMouseLeave);
			coordinates.current = { ...initialCoordinates };
		};
	}, []);

	useEffect(() => {
		checkBorderLimits();
	}, [parentCoordinates]);

	const checkBorderLimits = () => {
		const { x, y } = parentCoordinates;
		let newCoordinateX: number = lastCoordinates.current.x;
		let newCoordinateY: number = lastCoordinates.current.y;
		if (lastCoordinates.current.y + 40 > y) {
			newCoordinateY = y - 40;
		}
		if (lastCoordinates.current.x + targetContainer.current.offsetWidth > x) {
			newCoordinateX = x - targetContainer.current.offsetWidth;
		}
		if (lastCoordinates.current.x < 0) {
			newCoordinateX = 0;
		}
		if (lastCoordinates.current.y < 2) {
			newCoordinateY = 2;
		}
		targetContainer.current.style.left = `${newCoordinateX}px`;
		targetContainer.current.style.top = `${newCoordinateY}px`;
		lastCoordinates.current.x = newCoordinateX;
		lastCoordinates.current.y = newCoordinateY;
		onDragEnd?.(lastCoordinates.current);
	};

	const handleOnMouseEnter = (event: MouseEvent) => {
		event.preventDefault();
		targetContainer.current.removeEventListener('mouseup', handleOnDragMouseUp);
		targetContainer.current.removeEventListener('mousedown', handleOnDragMouseDown);
	};

	const handleOnMouseLeave = (event: MouseEvent) => {
		event.preventDefault();
		targetContainer.current.addEventListener('mouseup', handleOnDragMouseUp);
		targetContainer.current.addEventListener('mousedown', handleOnDragMouseDown);
	};

	const handleOnDragMouseUp = (event: MouseEvent) => {
		event.preventDefault();
		if (event.type === 'mouseup') {
			isContainerClicked.current = false;
			lastCoordinates.current.x = targetContainer.current.offsetLeft;
			lastCoordinates.current.y = targetContainer.current.offsetTop;

			checkBorderLimits();
		}
	};

	const handleOnDragMouseMove = (event: MouseEvent) => {
		if (!isContainerClicked.current) {
			return;
		}

		event.preventDefault();

		const { clientX, clientY } = event;
		const newCoordinateX = clientX - coordinates.current.x + lastCoordinates.current.x;
		const newCoordinateY = clientY - coordinates.current.y + lastCoordinates.current.y;

		targetContainer.current.style.top = `${newCoordinateY}px`;
		targetContainer.current.style.left = `${newCoordinateX}px`;
	};

	const handleOnDragMouseDown = (event: MouseEvent) => {
		event.preventDefault();

		isContainerClicked.current = true;

		const { clientX, clientY } = event;
		coordinates.current.x = clientX;
		coordinates.current.y = clientY;
	};
};

export { useDraggable };
